
import dayjs from "dayjs";
import _ from "lodash";
import { enqueueSnackbar } from "notistack";
import { useState, useEffect, useMemo, useRef } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import * as yup from "yup";
import axios from "axios";
import { API } from "../helpers/constants";
import { handleAxiosErrors, logoutUnauthorizedUser } from "../helpers/errorHandling";
import { getRouteUrl } from "../helpers/routing/getRouteUrl";
import useUserData from "./user/useUserData";
import { QUESTIONNAIRE_BUILDER } from "../locales/pages/namespaces";
import questionnaireFieldKey from "../helpers/questionnaireFieldKey";
import { ROUTE_PAGE_QUESTIONNAIRE_PREVIEW_BY_HEADER } from "../modules/issues/routes/routes";

const toBool = (v) => v === "1" || v === 1 || v === true;

const safeRegex = (regex) => {
	try {
		return new RegExp(regex);
	} catch {
		return null;
	}
};

// Normalize scalar values that sometimes arrive as arrays or as quoted strings
const normalizeSingleValue = (v) => {
	if (Array.isArray(v)) v = v.length > 0 ? v[0] : "";
	if (v == null) return "";
	if (typeof v === 'string') {
		const trimmed = v.trim();
		// If backend stored a JSON-encoded string like '"386"', strip surrounding quotes
		if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
			return trimmed.slice(1, -1);
		}
		return trimmed;
	}
	return String(v);
};

const buildInitialValuesAndSchema = ({ items, t }) => {
 	const initVals = {};
 	const shape = {};

	// For unanswered repeatable questions, multiple rows share the same backend ItemId.
	// We should key the Formik value by the QUESTION key (q:<ItemId>) and index by RepeatNum.
	// This prevents "missing q:48" when rows carry runtime ids.
	const getQuestionAggregateKey = (it) => {
		if (!it) return "";
		// Attributes are independent rows. Use their own stable field key.
		// - answered: d:<DetailId>
		// - dynamic: whatever questionnaireFieldKey produces (unique runtime)
		if (String(it?.Type) === 'attribute') return questionnaireFieldKey(it);

		// Answered + non-repeatable questions must validate under d:<DetailId>.
		if (it?.DetailId != null && !toBool(it?.Repeatable)) return questionnaireFieldKey(it);
		
		// Cloned instances (no DetailId) of non-repeatable questions that belong to a repeatable group.
		// IMPORTANT: every clone has a unique uiId (uic:<uuid>), so we use the instance-scoped key
		// u:<uiId>. This is what renderField also uses, so values, validation, and submit all align.
		// Previously we used the shared q:<ItemId> here, which caused all group instances to share one value.
		if (it?.DetailId == null && it?._isClone && !toBool(it?.Repeatable)) {
			return questionnaireFieldKey(it); // returns u:<uiId> when uiId is present
		}

		const isRepeatable = toBool(it?.Repeatable);
		// Repeatable QUESTIONS aggregate under q:<ItemId>
		if (isRepeatable) {
			const base = it?.ItemId ?? it?.id;
			return base != null ? `q:${String(base)}` : questionnaireFieldKey(it);
		}
		// Non-repeatable answered question: keep row identity (d:<DetailId>)
		if (it.DetailId != null) return questionnaireFieldKey(it);
		// Non-repeatable unanswered question: stable q:<ItemId>
		const base = it?.ItemId ?? it?.id;
		return base != null ? `q:${String(base)}` : questionnaireFieldKey(it);
	};

 	// Precompute how many instances per Formik key exist in the dataset (based on RepeatNum).
 	// This lets us validate repeatable questions correctly: required means every instance is filled.
 	const maxRepeatNumByKey = new Map();
 	(items || []).forEach((it) => {
 		if (!it) return;
 		if (it.Type !== 'question' && it.Type !== 'attribute') return;
 		if (!toBool(it?.Repeatable)) return;
		const k = getQuestionAggregateKey(it);
		if (!k) return;
		const rn = Math.max(1, Number(it?.RepeatNum ?? 1));
		const prev = maxRepeatNumByKey.get(k) ?? 0;
		if (rn > prev) maxRepeatNumByKey.set(k, rn);
 	});

	// For initial values we need uniqueness per backend row.
	// In answered mode, uniqueness is (ItemId + DetailId). In unanswered, it's ItemId.
	// Also include attributes, because answered payload can include attribute rows.
	const formItems = (items || []).filter((it) => it && (it.Type === "question" || it.Type === "attribute"));
	formItems.forEach((it) => {
		const key = getQuestionAggregateKey(it);
		if (!key) return;

		const typeId = Number(it?.TypeId);
		const isRepeatableField = toBool(it?.Repeatable);

		// ---- initial value ----
		if (isRepeatableField) {
			if (!Array.isArray(initVals[key])) initVals[key] = [];
			const repeatNum = Math.max(1, Number(it.RepeatNum || 1));
			const idx = repeatNum - 1;
			let raw = it?.Value ?? "";
			if (typeId === 4 || typeId === 5) {
				// Select / Radio: normalize single scalar (may arrive as array or quoted string)
				initVals[key][idx] = normalizeSingleValue(raw);
			} else if (typeId === 6 || typeId === 9) {
				// Checkbox / Multiselect: ensure array of normalized strings
				if (Array.isArray(raw)) initVals[key][idx] = raw.map(normalizeSingleValue);
				else initVals[key][idx] = raw ? [normalizeSingleValue(raw)] : [];
			} else if (typeId === 3) {
				initVals[key][idx] = raw ? dayjs(raw) : "";
			} else if (typeId === 10 || typeId === 11) {
				initVals[key][idx] = raw ? dayjs(raw) : "";
			} else {
				initVals[key][idx] = raw ?? "";
			}
			if (initVals[key].length === 0) initVals[key] = [""];
		} else {
			switch (typeId) {
				case 6: // checkbox
				case 9: // multiselect
					// attribute multiselect can arrive as scalar (id) in answered payload; normalize to array of strings
					if (it.Type === "attribute") {
						const raw = it?.Value;
						if (Array.isArray(raw)) initVals[key] = raw.map(normalizeSingleValue);
						else initVals[key] = raw ? [normalizeSingleValue(raw)] : [];
					} else {
					const raw = it?.Value;
					if (Array.isArray(raw)) initVals[key] = raw.map(normalizeSingleValue);
					else initVals[key] = raw ? [normalizeSingleValue(raw)] : [];
				}
				break;
				case 3: // date
					initVals[key] = it?.Value ? dayjs(it.Value) : "";
					break;
				case 4: // select
				case 5: // radio
					// Make sure we store a plain string value for select/radio
					initVals[key] = normalizeSingleValue(it?.Value);
					break;
				case 10: // time
					initVals[key] = it?.Value ? dayjs(it.Value, "HH:mm:ss") : "";
					break;
				case 11: // datetime
					initVals[key] = it?.Value ? dayjs(it.Value) : "";
					break;
				case 12: // signature
					initVals[key] = it?.Value ?? "";
					break;
				default:
					initVals[key] = it?.Value ?? "";
			}
		}

		// ---- validation ----
		let rule;
		switch (typeId) {
			case 1: // Text
				rule = yup.string();
				if (it?.Required === "1") rule = rule.required(t("Required"));
				if (it?.Regex) {
					const rx = safeRegex(it.Regex);
					// if (rx) rule = rule.matches(rx, it?.RegexMessage || t("Invalid format"));
					const msg =
						typeof it?.RegexMessage === "string" && it.RegexMessage.trim() !== ""
							? it.RegexMessage.trim()
							: t("Invalid format");
					rule = rule.matches(rx, msg);
				}
				if (isRepeatableField) rule = yup.array().of(rule);
				break;
			case 4: // Select
				rule = yup.string().transform((v) => (typeof v === 'string' ? v.trim() : v));
				if (it?.Required === "1") rule = rule.required(t("Required"));
				if (isRepeatableField) {
					const maxRn = maxRepeatNumByKey.get(key) ?? 1;
					// per-index errors so UI can show required under each repeat instance
					rule = yup
					.array()
					.min(maxRn, t("Required"))
					.of(
						yup
						.string()
						.transform((v) => (typeof v === 'string' ? v.trim() : v))
						.required(t("Required"))
					);
				}
				break;
			case 2: // Number
				rule = yup.number().typeError("Must be a number");
				// if (it?.Min) rule = rule.min(Number(it.Min));
				// if (it?.Max) rule = rule.max(Number(it.Max));
				if (it?.Min) {
					rule = rule.min(
						Number(it.Min),
						t("Value must be greater than or equal to {{min}}", { min: it.Min })
					);
				}
				
				if (it?.Max) {
					rule = rule.max(
						Number(it.Max),
						t("Value must be less than or equal to {{max}}", { max: it.Max })
					);
				}
				if (it?.Required === "1") rule = rule.required(t("Required"));
				if (isRepeatableField) rule = yup.array().of(yup.number().typeError("Must be a number"));
				break;
			case 3: // Date
				rule = yup.mixed();
				if (it?.Required === "1") rule = rule.required(t("Required"));
				break;
			case 5: // Radio
				rule = yup.string();
				if (it?.Required === "1") rule = rule.required(t("Required"));
				break;
			case 6: // Checkbox
				rule = yup.array();
				if (it?.Required === "1") rule = rule.min(1, t("Required"));
				break;
			case 8: // Textarea
				rule = yup.string();
				if (it?.Required === "1") rule = rule.required(t("Required"));
				if (it?.Regex) {
					const rx = safeRegex(it.Regex);
					if (rx) rule = rule.matches(rx, it?.RegexMessage || t("Invalid format"));
				}
				if (isRepeatableField) rule = yup.array().of(rule);
				break;
			case 9: // Multiselect
				rule = yup.array();
				if (it?.Required === "1") rule = rule.min(1, t("Required"));
				if (isRepeatableField) rule = yup.array().of(yup.array());
				break;
			case 10: // Time
				rule = yup.mixed();
				if (it?.Required === "1") rule = rule.required(t("Required"));
				break;
			case 11: // DateTime
				rule = yup.mixed();
				if (it?.Required === "1") rule = rule.required(t("Required"));
				break;
			case 12: // Signature
				rule = yup.string().nullable().transform((v) => (typeof v === 'string' ? v.trim() : v));
				if (it?.Required === "1") rule = rule.required(t("Required"));
				break;
			default:
				rule = yup.mixed();
				
				if (it?.Required === "1") rule = rule.required(t("Required"));
				if (it?.Regex) {
					const rx = safeRegex(it.Regex);
					// if (rx) rule = rule.matches(rx, it?.RegexMessage || t("Invalid format"));
					const msg =
						typeof it?.RegexMessage === "string" && it.RegexMessage.trim() !== ""
							? it.RegexMessage.trim()
							: t("Invalid format");
					rule = rule.matches(rx, msg);
				}
		}

		shape[key] = rule;

		if (toBool(it?.AddComment)) {
			// Comments are UI fields. For repeatable questions we keep them per-instance (unique key)
			// to avoid collisions inside repeatable groups.
			const uiKey = isRepeatableField ? questionnaireFieldKey(it) : key;
			initVals[`${uiKey}_comment`] = "";
			shape[`${uiKey}_comment`] = yup.string();
		}

		// Initialize _attachments key for any field that supports attachments (or might get a RuleId).
		// We always initialize it (even when AddAttachment="0") so RuleId-driven rules can validate it.
		// Validation for required is added dynamically via activeRules in usePreviewForm (not here).
		{
			const uiKey = isRepeatableField ? questionnaireFieldKey(it) : key;
			if (initVals[`${uiKey}_attachments`] === undefined) {
				initVals[`${uiKey}_attachments`] = [];
				shape[`${uiKey}_attachments`] = yup.array();
			}
		}

		// UI-only: NewValue for Select when options include AddNewValue
		if (typeId === 4 && !isRepeatableField) {
			const uiKey = questionnaireFieldKey(it);
			// initVals[`${uiKey}_newValue`] = "";
			
			const backendNewValue = it?.NewValue ?? it?.newValue ?? "";
			initVals[`${uiKey}_newValue`] = backendNewValue ?? "";
			shape[`${uiKey}_newValue`] = yup.string();
		}
	});

	// --- Group title editing (Accordion title) ---
	// We store group title edits in formik as well, using a dedicated, stable key.
	// This is intentionally separate from question/attribute keys.
	const groupItems = (items || []).filter((it) => it && it.Type === "group");
	groupItems.forEach((g) => {
		const groupKey = `groupTitle:${questionnaireFieldKey(g)}`;
		if (!groupKey) return;
		if (initVals[groupKey] == null) initVals[groupKey] = g?.Description ?? "";
		shape[groupKey] = yup.string();
	});

	initVals.finalization = false;
	// Signature is now type 12 — a regular field. Required is driven by the field's Required property.
	// The static global signature key and finalization-based validation are kept here commented for reference.
	// initVals.signature = "";
	// shape.signature = yup
	// 	.string()
	// 	.nullable()
	// 	.transform((v) => (typeof v === 'string' ? v.trim() : v))
	// 	.when('finalization', {
	// 		is: (v) => toBool(v),
	// 		then: (schema) => schema.required(t('Required')),
	// 		otherwise: (schema) => schema,
	// 	});

	return { initialValues: initVals, validationSchema: yup.object().shape(shape) };
};

export default function usePreviewForm({ questionnaireId, headerId, data: initialData,
										   // geoStart, geoEnd, // todo:: iuncomment when we have api key for geolocation
										   extraItems,
										   activeRules,
									   }) {
 	const [data, setData] = useState(initialData || null);
 	const [loading, setLoading] = useState(!initialData);
 	const { t } = useTranslation(QUESTIONNAIRE_BUILDER);
	const { token } = useUserData();
	// Reinitialize only when the backend dataset identity changes (answered vs new form),
	// not when UI-only items (clones) change.
	const lastDataMarkerRef = useRef(null);
	const dataMarker = `${String(data?.id ?? '')}:${String(data?.HeaderId ?? headerId ?? '')}:${String(questionnaireId ?? '')}`;
	const shouldReinitialize = !lastDataMarkerRef.current || lastDataMarkerRef.current !== dataMarker;
	const history = useHistory()
	
	
	console.log("usePreviewForm", { questionnaireId, headerId, initialData, data, shouldReinitialize, activeRules });
 	// Load source:
 	// - Create preview: initialData provided => use it, no API.
 	// - Fill first time: questionnaireId provided => questionnaire/get/data
 	// - Edit answered / locked: headerId provided => questionnaire-data/get/data
 	useEffect(() => {

		if (initialData) return;
 		
 		if (questionnaireId && questionnaireId !== 'null') {
 			setLoading(true);
 			axios
 			.post(`${API}/en/questionnaire/get/data`, { QuestionnaireId: questionnaireId, isPreview: 1 }, {
				headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			})
 			.then((res) => {
 				const d = res.data.data;
 				setData({
 					id: questionnaireId,
 					HeaderId: d.HeaderId,
 					GlobalComment: d.Comment,
 					Description: d.Description,
 					Items: d.Items,
 					Status: d.Status ?? "",
 					ChecklistType: d.ChecklistType ?? "checked",
					Signature: d.Signature || "",
 				});
 				setLoading(false);
 			})
 			.catch(() => setLoading(false));
 			return;
 		}
 		
 		if (!headerId) return;
 		
 		setLoading(true);
 		axios
		.post(`${API}/en/questionnaire-data/get/data`, { HeaderId: headerId }, {
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
		})
		.then((res) => {
			const d = res.data?.data;
			setData({
				id: d?.id,
				HeaderId: d?.HeaderId,
				GlobalComment: d?.Comment,
				Description: d?.Description,
				Items: d?.Items || [],
				Status: d?.Status ?? "",
				ChecklistType: d?.ChecklistType ?? "checked",
				Signature: d?.Signature || d?.signature || "",
				Finalization: d.Finalization || false,
			});
			setLoading(false);
		})
		.catch(() => setLoading(false));
	}, [initialData, questionnaireId, headerId, token]);

	const { initialValues, validationSchema } = useMemo(() => {

		const mergedItems = [
			...(data?.Items || []),
			...(Array.isArray(extraItems) ? extraItems : []),
		];

		const built = buildInitialValuesAndSchema({
			items: mergedItems,
			t,
		});

		// εγγύηση βασικών keys
		// NOTE: signature is now type 12 field — initial value is set per-field above.
		const nextInitialValues = {
			...built.initialValues,
			finalization: false,
		};

		// --- Dynamic validation overlay for RuleId ---
		// RuleId=1 → comment + attachment required
		// RuleId=2 → attachment required
		// RuleId=3 → comment required
		// We merge these on top of the static schema produced by buildInitialValuesAndSchema.
		const ruleShape = {};
		const rules = activeRules && typeof activeRules === 'object' ? activeRules : {};
		Object.entries(rules).forEach(([uiKey, ruleId]) => {
			if (!uiKey || !ruleId) return;
			const rule = Number(ruleId);
			const commentRequired = rule === 1 || rule === 3;
			const attachmentRequired = rule === 1 || rule === 2;

			if (commentRequired) {
				ruleShape[`${uiKey}_comment`] = yup.string().required(t('Required Comment'));
				if (nextInitialValues[`${uiKey}_comment`] === undefined) {
					nextInitialValues[`${uiKey}_comment`] = '';
				}
			}
			if (attachmentRequired) {
				ruleShape[`${uiKey}_attachments`] = yup.array().min(1, t('Required Attachment'));
				if (nextInitialValues[`${uiKey}_attachments`] === undefined) {
					nextInitialValues[`${uiKey}_attachments`] = [];
				}
			}
		});

		// Merge static schema shape with dynamic rule shape (rule shape takes precedence)
		const baseShape = built.validationSchema.fields ?? {};
		const mergedValidationSchema = yup.object().shape({ ...baseShape, ...ruleShape });

		return {
			initialValues: nextInitialValues,
			validationSchema: mergedValidationSchema,
		};
	}, [data, extraItems, t, headerId, activeRules]);

	useEffect(() => {
		lastDataMarkerRef.current = dataMarker;
	}, [dataMarker]);
 	
 	const onSubmit = async (values) => {
 		console.log("FORM SUBMIT values", values);
 		try {
 			// const startCoords = geoStart.coords; // todo:: iuncomment when we have api key for geolocation
 			const { finalization } = values;

 			// End geolocation is required only on finalization.
 			let endLocation = null;
			// todo:: iuncomment when we have api key for geolocation
			// if (finalization) {
 			// 	endLocation = await geoEnd.getLocationAndAddress();
 			// }
			
			//  todo:: iuncomment when we have api key for geolocation
 			// if (!startCoords?.lat || (finalization && !endLocation?.coords?.lat)) {
 			// 	console.warn("Geolocation not ready", { startCoords, endLocation, finalization });
 			// 	return;
 			// }

 			const items = [
 				...(Array.isArray(data?.Items) ? data.Items : []),
 				...(Array.isArray(extraItems) ? extraItems : []),
 			];


 			// Debug: verify that dynamic attribute items exist & carry AttributeId
 			// const debugAttrItems = (items || []).filter((it) => it?._isAttributeField === true);
 			// if (debugAttrItems.length > 0) {
 			// 	console.log("SUBMIT DEBUG attribute items sample", debugAttrItems.slice(0, 5));
 			// }

 			// --- Build backend-style payload (object with Items[]) ---
 			// We must map UI ids (e.g. rgc:...) to stable per-submit DetailIds,
 			// so ParentId can reference the correct group instance.
 			const detailIdByUiId = new Map();
 			// New rows must be sent with an alphanumeric DetailId (per your note), but safe for ParentId refs.
 			let nextTempDetailId = 1;
 			const ensureDetailId = (it) => {
 				const uiId = String(it?.id ?? "");
 				if (!uiId) return null;
 				// Backend answered rows: keep as-is
 				if (it?.DetailId != null) return String(it.DetailId);
 				if (detailIdByUiId.has(uiId)) return detailIdByUiId.get(uiId);
 				const tmp = `tmp-${nextTempDetailId++}`;
 				detailIdByUiId.set(uiId, tmp);
 				return tmp;
 			};

 			// Helper: compute group instance index (RepeatNum) per repeatable group template.
 			const computeGroupRepeatNums = (allItems) => {
 				const repeatNumByGroupUiId = new Map();
 				const groupInstancesByTemplate = new Map();
 				(allItems || []).forEach((it) => {
 					if (!it || it.Type !== 'group') return;
 					const templateId = String(it?._cloneOfId ?? it.id);
 					if (!groupInstancesByTemplate.has(templateId)) groupInstancesByTemplate.set(templateId, []);
 					groupInstancesByTemplate.get(templateId).push(it);
 				});
 				groupInstancesByTemplate.forEach((list) => {
 					list.sort((a, b) => {
 						if (!!a?._isClone === !!b?._isClone) return String(a.id).localeCompare(String(b.id));
 						return a?._isClone ? 1 : -1;
 					});
 					list.forEach((g, idx) => repeatNumByGroupUiId.set(String(g.id), String(idx + 1)));
 				});
 				return repeatNumByGroupUiId;
 			};

 			const repeatNumByGroupUiId = computeGroupRepeatNums(items);

			// For payload ParentId:
			// - If backend already provided a ParentId (answered datasets often do), keep it.
			// - Otherwise resolve it through the in-memory UI tree.
			// - If neither exists, send null.
			const payloadParentIdFor = (it) => {
				const answered = !!(data?.HeaderId || headerId);

				// IMPORTANT: Clone rows (new group instances added in edit mode) have _isClone=true and
				// DetailId=null. Their parent is also a clone group (no backend DetailId), so we MUST
				// resolve through ensureDetailId to get the tmp-N id of the clone group — NOT backendParentId
				// which would point to the original template group's backend id.
				const isNewRow = it?.DetailId == null; // row has no backend identity yet

				if (answered && !isNewRow) {
					// Existing backend row: use backendParentId or raw ParentId (both are backend DetailIds).
					if (it?.backendParentId != null && String(it.backendParentId) !== '') {
						return String(it.backendParentId);
					}
					if (it?.ParentId != null && String(it.ParentId) !== '') {
						return String(it.ParentId);
					}
					return null;
				}

				// New rows (clones or unanswered): resolve parent via UI tree → ensureDetailId.
				// This correctly produces tmp-N for clone group parents.
				if (!it?.ParentId) return null;

				const parentItem = (items || []).find(
					(x) => x && String(x.id) === String(it.ParentId)
				);

				if (!parentItem) return null;
				return ensureDetailId(parentItem);
			};

			// For payload DetailId / id:
			// - Answered rows: keep backend DetailId and backend ItemId/id.
			// - New rows: generate tmp DetailId and use ItemId/id when present.
			const payloadDetailIdFor = (it, { forcedUiId } = {}) => {
				if (it?.DetailId != null) return String(it.DetailId);
				// allow forcing a unique key for tmp generation (repeatable rows)
				const tmp = forcedUiId ? ensureDetailId({ ...it, id: forcedUiId }) : ensureDetailId(it);
				return tmp ?? null;
			};
			const payloadIdFor = (it, { isAttributeRow } = {}) => {
				if (isAttributeRow) return null;
				// Prefer backend ItemId when available
				if (it?.ItemId != null) return String(it.ItemId);
				// Answered rows may carry backend id directly
				if (it?.id != null) return String(it.id);
				return null;
			};

 			const payloadObject = {
 				id: data?.id != null ? String(data.id) : (questionnaireId != null ? String(questionnaireId) : null),
 				HeaderId: data?.HeaderId ?? headerId ?? null,
 				Comment: data?.GlobalComment ?? null,
 				Finalization: !!finalization,
 				Signature: values?.signature ?? "",
				// todo:: iuncomment when we have api key for geolocation
 				// start_lat: startCoords?.lat ? String(startCoords.lat) : null,
 				// start_lon: startCoords?.lng ? String(startCoords.lng) : null,
 				// start_address: geoStart?.address ?? null,
 				end_lat: finalization && endLocation?.coords?.lat ? String(endLocation?.coords?.lat) : null,
 				end_lon: finalization && endLocation?.coords?.lng ? String(endLocation?.coords?.lng) : null,
 				end_address: finalization ? (endLocation?.address ?? null) : null,
 				Items: [],
 			};

 			// Include group titles as rows too (so backend can persist per-group instance titles).
 			(items || []).forEach((g) => {
 				if (!g || g.Type !== 'group') return;
 				const detailId = payloadDetailIdFor(g);
 				const repeatNumForRow = repeatNumByGroupUiId.get(String(g.id)) ?? String(g?.RepeatNum ?? '1');
 				const titleKey = `groupTitle:${questionnaireFieldKey(g)}`;
 				const titleVal = values?.[titleKey] ?? g?.Description ?? "";
 				payloadObject.Items.push({
 					DetailId: detailId,
 					id: payloadIdFor(g),
 					ParentId: payloadParentIdFor(g),
 					RepeatNum: String(repeatNumForRow),
 					AttributeId: null,
 					Value: titleVal ?? null,
 				});
 			});


 			items.forEach((item) => {
				if (!item) return;
				if (item.Type !== "question" && item.Type !== "attribute") return;

				// Safety: never submit attribute rows whose parent question no longer exists in the current UI items.
				// NOTE: In answered questionnaires, Preview may remap ids; attributes can carry either:
				// - ParentId = UI id of parent question, OR
				// - backendParentId = backend DetailId of parent question.
				// We must accept both, otherwise valid attributes get dropped.
				if (item.Type === 'attribute') {
					const pid = item?.ParentId;
					const bpid = item?.backendParentId;

					// If we have neither, it's truly orphan.
					if (!pid && !bpid) return;

					const parentQ = (items || []).find((x) => {
						if (!x || x.Type !== 'question') return false;
						// 1) typical UI wiring: attribute.ParentId points to parent question id
						if (pid && String(x.id) === String(pid)) return true;
						// 2) answered wiring: attribute.backendParentId points to parent question DetailId
						if (bpid && x?.DetailId != null && String(x.DetailId) === String(bpid)) return true;
						// 3) fallback: sometimes ParentId is already a backend DetailId
						if (pid && x?.DetailId != null && String(x.DetailId) === String(pid)) return true;
						return false;
					});

					if (!parentQ) return;
				}
				const isRepeatable = toBool(item?.Repeatable);
			const isAttributeRow = item.Type === "attribute";
			const typeId = Number(item?.TypeId);
			// Values/validation for repeatables are stored under aggregate key (q:<ItemId>),
			// while UI-only fields (comments/attachments) use per-instance key.
			const uiKey = questionnaireFieldKey(item);
			// IMPORTANT: Cloned questions now use instance-scoped keys (u:<uiId>) for both render and submit.
			// We always fall through to uiKey for non-repeatable questions (including clones).
			const key = isRepeatable
				? (item?.DetailId != null ? uiKey : (`q:${String(item?.ItemId ?? item?.id)}`))
				: uiKey;
				let formVal = values?.[key];
				// Backward compatibility: older versions used plain numeric keys (e.g. "48").
				if (formVal === undefined) {
					if (key?.startsWith('q:')) formVal = values[key.slice(2)];
					else if (key?.startsWith('d:')) formVal = values[key.slice(2)];
					else if (key?.startsWith('r:')) formVal = values[key.slice(2)];
					else if (key?.startsWith('u:')) formVal = values[key.slice(2)];
				}

				const groupInstanceRepeatNum = (() => {
					// If the parent is a group instance, use its instance number.
					const pid = item?.ParentId;
					if (!pid) return null;
					return repeatNumByGroupUiId.get(String(pid)) ?? null;
				})();

				// Repeatable questions/attributes:
				// Backend-required format: EACH answer becomes a new row with its own DetailId and RepeatNum=1..N.
				// This applies to BOTH unanswered and answered datasets.
				if (isRepeatable) {
					const arrRaw = Array.isArray(formVal) ? formVal : (formVal == null ? [""] : [formVal]);
					// even if empty, send 1 row (matches UI that always has at least one input)
					const safeArr = arrRaw.length > 0 ? arrRaw : [""];
					safeArr.forEach((rowVal, idx) => {
						let value = rowVal;
						if (dayjs.isDayjs(value)) {
							if (typeId === 10) value = value.format("HH:mm:ss");
							else if (typeId === 11) value = value.format("YYYY-MM-DD HH:mm:ss");
							else value = value.format("YYYY-MM-DD");
						}
						if (typeId === 6 || typeId === 9) value = Array.isArray(value) ? value : [];
						if (isAttributeRow && Array.isArray(value) && value.length === 1) value = value[0];

						payloadObject.Items.push({
							DetailId: payloadDetailIdFor(item, { forcedUiId: `${String(item?.id ?? '')}#r${idx + 1}` }),
							id: payloadIdFor(item, { isAttributeRow }),
							ParentId: payloadParentIdFor(item),
							RepeatNum: String(idx + 1),
							AttributeId: isAttributeRow ? (item?.AttributeId != null ? String(item.AttributeId) : null) : null,
							Value: value ?? null,
							...(typeId === 12 ? { isSignature: 1 } : {}),
						});
					});
					return;
				}

				// Non-repeatable: single row
				let value = (() => {
					if (dayjs.isDayjs(formVal)) {
						if (typeId === 10) return formVal.format("HH:mm:ss");
						if (typeId === 11) return formVal.format("YYYY-MM-DD HH:mm:ss");
						return formVal.format("YYYY-MM-DD");
					}
					if (typeId === 6 || typeId === 9) return Array.isArray(formVal) ? formVal : [];
					return formVal ?? "";
				})();
				if (isAttributeRow && Array.isArray(value) && value.length === 1) value = value[0];

				// 1 unified payload item
				const payloadItem = {
					DetailId: payloadDetailIdFor(item),
					id: payloadIdFor(item, { isAttributeRow }),
					ParentId: payloadParentIdFor(item),
					RepeatNum: groupInstanceRepeatNum ?? String(item?.RepeatNum ?? "1"),
					AttributeId: isAttributeRow
						? (item?.AttributeId != null ? String(item.AttributeId) : null)
						: null,
					Value: value ?? null,
					...(typeId === 12 ? { isSignature: 1 } : {}),
				};

				// --- NewValue (inline) ---
				if (Number(item?.TypeId) === 4) {
					const newValueKey = `${uiKey}_newValue`;
					const rawNewValue = values?.[newValueKey];
					const selectedOpt = Array.isArray(item?.Options)
						? item.Options.find((o) => String(o?.id) === String(value))
						: null;
					const allowNewValue = toBool(selectedOpt?.isOther);
					if (allowNewValue && rawNewValue != null && String(rawNewValue).trim() !== "") {
						payloadItem.NewValue = String(rawNewValue).trim();
					}
				}

				// --- Comment (inline) ---
				if (toBool(item?.AddComment)) {
					const commentKey = `${uiKey}_comment`;
					const commentVal = values?.[commentKey];
					if (commentVal != null && String(commentVal).trim() !== "") {
						payloadItem.Comment = String(commentVal).trim();
					}
				}

				// --- Attachments (inline) ---
				// if (toBool(item?.AddAttachment)) {
				// 	const attachmentsKey = `${uiKey}_attachments`;
				// 	const attachmentsVal = values?.[attachmentsKey];
				// 	const hasFiles = Array.isArray(attachmentsVal)
				// 		? attachmentsVal.length > 0
				// 		: !!attachmentsVal;
				//
				// 	if (hasFiles) {
				// 		payloadItem.Attachments = attachmentsVal;
				// 	}
				// }
				// --- Attachments (inline) ---
				if (toBool(item?.AddAttachment)) {
					const attachmentsKey = `${uiKey}_attachments`;
					const attachmentsVal = values?.[attachmentsKey];
					const arr = Array.isArray(attachmentsVal)
						? attachmentsVal
						: (attachmentsVal ? [attachmentsVal] : []);

					// FileUpload stores { file, url }. Backend expects UploadedFile, so we keep File[] here.
					const files = arr
					.map((x) => (x?.file instanceof File ? x.file : (x instanceof File ? x : null)))
					.filter(Boolean);

					if (files.length > 0) {
						payloadItem.Attachments = files;
					}
				}


				// 1 push
				payloadObject.Items.push(payloadItem);
 			});

 			console.log("QUESTIONNAIRE SUBMIT PAYLOAD (object)", payloadObject);
 			// Submit
			try {
				const hasAnyFiles = (payloadObject?.Items || []).some(
					(it) => Array.isArray(it?.Attachments) && it.Attachments.length > 0
				);

				if (!hasAnyFiles) {
					const response = await axios.post(`${API}/en/questionnaire-data/store/data`, payloadObject);
					enqueueSnackbar(t(`Questionnaire ${!payloadObject.HeaderId ? 'created' : 'updated'} successfully!`), {
						variant: 'success',
						autoHideDuration: 3000,
					});
					
						history.push(getRouteUrl(ROUTE_PAGE_QUESTIONNAIRE_PREVIEW_BY_HEADER, { headerId: response?.data?.data?.HeaderId ?? payloadObject.HeaderId }));
				
					console.log('success', response?.data);
					return;
				}

				// Build a safe copy for multipart fields (no File objects inside)
				const safePayload = {
					...payloadObject,
					Items: (payloadObject?.Items || []).map((it) => {
						if (!it) return it;
						if (!Array.isArray(it.Attachments) || it.Attachments.length === 0) return it;
						return {
							...it,
							Attachments: it.Attachments.map((f) => f?.name).filter(Boolean),
						};
					}),
				};

				const fd = new FormData();

				// Put scalar/top-level fields as normal multipart fields so $request->all() matches backend expectations.
				fd.append('id', safePayload.id ?? '');
				if (safePayload.HeaderId != null) fd.append('HeaderId', String(safePayload.HeaderId));
				if (safePayload.Comment != null) fd.append('Comment', String(safePayload.Comment));
				fd.append('Finalization', safePayload.Finalization ? '1' : '0');
				if (safePayload.Signature != null) fd.append('Signature', String(safePayload.Signature));
				if (safePayload.start_lat != null) fd.append('start_lat', String(safePayload.start_lat));
				if (safePayload.start_lon != null) fd.append('start_lon', String(safePayload.start_lon));
				if (safePayload.start_address != null) fd.append('start_address', String(safePayload.start_address));
				if (safePayload.end_lat != null) fd.append('end_lat', String(safePayload.end_lat));
				if (safePayload.end_lon != null) fd.append('end_lon', String(safePayload.end_lon));
				if (safePayload.end_address != null) fd.append('end_address', String(safePayload.end_address));

				// Items as multipart fields (Value/Comment/ids) – keep Attachments as filenames here.
				(safePayload?.Items || []).forEach((it, idx) => {
					if (!it) return;
					if (it.DetailId != null) fd.append(`Items[${idx}][DetailId]`, String(it.DetailId));
					if (it.id != null) fd.append(`Items[${idx}][id]`, String(it.id));
					if (it.ParentId != null) fd.append(`Items[${idx}][ParentId]`, String(it.ParentId));
					if (it.RepeatNum != null) fd.append(`Items[${idx}][RepeatNum]`, String(it.RepeatNum));
					if (it.AttributeId != null) fd.append(`Items[${idx}][AttributeId]`, String(it.AttributeId));
					if (it.Value != null) {
						// for arrays, backend expects comma separated (it already does implode), so send string
						fd.append(`Items[${idx}][Value]`, Array.isArray(it.Value) ? it.Value.join(',') : String(it.Value));
					}
					if (it.Comment != null) fd.append(`Items[${idx}][Comment]`, String(it.Comment));
					if (it.NewValue != null) fd.append(`Items[${idx}][NewValue]`, String(it.NewValue));
				});


				// Binary parts – nested keys so backend can read $item['Attachments'] as UploadedFile[]
				(payloadObject?.Items || []).forEach((it, idx) => {
					const files = it?.Attachments;
					if (!Array.isArray(files) || files.length === 0) return;
					files.forEach((file, j) => {
						fd.append(`Items[${idx}][Attachments][${j}]`, file, file.name);
					});
				});

				const response = await axios.post(`${API}/en/questionnaire-data/store/data`, fd, {
					headers: { 'Content-Type': 'multipart/form-data' },
				});

				enqueueSnackbar(t(`Questionnaire ${!payloadObject.HeaderId ? 'created' : 'updated'} successfully!`), {
					variant: 'success',
					autoHideDuration: 3000,
				});
					history.push(getRouteUrl(ROUTE_PAGE_QUESTIONNAIRE_PREVIEW_BY_HEADER, { headerId: response?.data?.data?.HeaderId ?? payloadObject.HeaderId }));
				
				console.log('success', response?.data);
				
			} catch (error) {
				enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
					variant: 'error',
					autoHideDuration: 3000,
				})
			}
 		} catch (err) {
 			console.error("Error submitting form:", err);
 		}
 	};

 	const formik = useFormik({
 		initialValues,
 		validationSchema,
 		onSubmit,
 		// IMPORTANT:
 		// We want initial values to appear when we load answered questionnaires,
 		// but we must avoid wiping values during UI cloning.
 		enableReinitialize: shouldReinitialize,
 	});



 	return {
 		formik,
 		loading,
 		data,
 		setData,
 		// geoStart, // todo:: iuncomment when we have api key for geolocation
 		// geoEnd,
 	};
 }

